// Provenance: repo:tds-reconciliation-portal:frontend/src/pages/RawDataPage.tsx (ADR-0005) —
// REBUILT on the estate tokens (ADR-0010). The behavioural spine carries: session-gated
// per-side visibility, row-grain duplicate selection scoped to the page, books-only review
// (26AS repeats are not processed and not mentioned — user decision 13-Aug), the by-company
// view with bulk verdicts, ledger corrections with undo (append-only, rebinding), keep-one,
// the withdraw prompt for verdicts stranded on an unreviewable side, and AIS/TIS as honest
// placeholders (spec §13-2, an open owner decision).
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Database,
  Pencil,
  Search,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { ApiError, api, patchJson, postJson } from '../../api/client'
import {
  usePeriodSummary,
  useRawSummary,
  useRuns,
  useUploadCompanies,
  useUploadRows,
  useUploads,
} from '../../api/hooks'
import { correctionChipLabel } from '../../api/keepOne'
import { noticeChip, rawNotices } from '../../api/rawNotices'
import type {
  Raw26ASItem,
  RawCompany,
  RawLedgerItem,
  RawSummaryEntry,
} from '../../api/types'
import { ChipStrip, StatChip } from '../../components/ChipStrip'
import { InfoTip } from '../../components/InfoTip'
import { KeepOnePanel } from '../../components/KeepOnePanel'
import { useDebounced } from '../../lib/useDebounced'
import { Btn, Modal } from '../../components/Modal'
import { Money } from '../../components/Money'
import { TablePager } from '../../components/TablePager'
import { Chip, type Tone } from '../../components/badges'
import { showToast } from '../../components/toast'
import { runTag } from '../../format'
import {
  type Side,
  useWorkspaceSession,
  visibleRawEntries,
} from '../../workspace/workspaceSession'

const PAGE_SIZE = 50
/** How many search hits open themselves — each open company mounts its own
 *  rows query, so opening EVERY match turned one search into hundreds. */
const AUTO_OPEN_LIMIT = 5
const fmt = new Intl.NumberFormat('en-IN')

const th = 'py-1.5 pr-2 text-left text-xs font-semibold text-muted-foreground'
const td = 'py-1.5 pr-2'
const input = 'rounded-md border border-border bg-background px-2 py-1 text-xs'

function LedgerEditModal({ uploadId, row, onClose }: {
  uploadId: string
  row: RawLedgerItem
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [payor, setPayor] = useState(row.payor)
  const [debit, setDebit] = useState(row.debit)
  const [narration, setNarration] = useState(row.narration ?? '')
  const [reason, setReason] = useState('')

  const save = useMutation({
    mutationFn: async () => {
      const edits: { field: string; value: string }[] = []
      if (payor.trim() !== row.payor) edits.push({ field: 'payor', value: payor.trim() })
      if (debit.trim() !== row.debit) edits.push({ field: 'debit', value: debit.trim() })
      if ((narration.trim() || null) !== (row.narration || null))
        edits.push({ field: 'narration', value: narration.trim() })
      for (const e of edits) {
        await patchJson(`/uploads/${uploadId}/ledger-rows/${row.row_index}`, {
          ...e,
          reason: reason.trim() || null,
        })
      }
      return edits.length
    },
    onSuccess: (n) => {
      showToast({
        color: n ? 'green' : 'blue',
        title: n
          ? `${n} correction(s) saved — original kept; the next run re-validates the books.`
          : 'No changes to save.',
      })
      void qc.invalidateQueries({ queryKey: ['upload-rows'] })
      void qc.invalidateQueries({ queryKey: ['upload-companies'] })
      void qc.invalidateQueries({ queryKey: ['raw-summary'] })
      onClose()
    },
    onError: (e) =>
      showToast({ color: 'red', title: e instanceof ApiError ? e.message : String(e) }),
  })

  const label = 'block text-xs font-medium'
  const field = 'mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm'
  return (
    <Modal opened onClose={onClose} title={`Correct books row #${row.row_index}`}>
      <div className="space-y-3 text-sm">
        <p className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Books only — 26AS is never editable. The original parsed value is kept and still
            downloadable; this correction is tracked (who/when/why) and replayed into the
            next reconciliation.
          </span>
        </p>
        <label className={label}>
          Party (payor)
          <input className={field} value={payor} onChange={(e) => setPayor(e.target.value)} />
        </label>
        <label className={label}>
          TDS amount (debit ₹)
          <input className={field} value={debit} onChange={(e) => setDebit(e.target.value)} />
        </label>
        <label className={label}>
          Narration
          <textarea className={field} rows={2} value={narration}
            onChange={(e) => setNarration(e.target.value)} />
        </label>
        <label className={label}>
          Reason for correction
          <textarea className={field} rows={2} value={reason}
            placeholder="e.g. OCR misread the party name — kept for the audit trail"
            onChange={(e) => setReason(e.target.value)} />
        </label>
        <div className="flex justify-end gap-2">
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Save correction
          </Btn>
        </div>
      </div>
    </Modal>
  )
}

type DupMenu = (row: number, verdict?: 'genuine' | 'not_duplicate' | null) => ReactNode

interface Selection {
  has: (row: number) => boolean
  toggle: (row: number) => void
  setMany: (rows: number[], on: boolean) => void
  clear: () => void
  list: number[]
}

/** Which rows are ticked, for one page of one list.
 *
 *  Keyed by `row_index` — the line in the uploaded file. It used to be keyed by
 *  the entry fingerprint, which is derived from content, so two physically
 *  identical lines shared one key and ticking row 28 ticked row 31. A row is
 *  what the file has and what the user is pointing at.
 *
 *  Deliberately scoped to the page and reset by `deps` — carrying ticks
 *  silently across a filter change is how the wrong four thousand rows get
 *  judged in one click. */
function useSelection(deps: unknown[]): Selection {
  const [sel, setSel] = useState<Set<number>>(new Set())
  const key = JSON.stringify(deps)
  const [seen, setSeen] = useState(key)
  if (seen !== key) {
    setSeen(key)
    if (sel.size) setSel(new Set())
  }
  return {
    has: (row) => sel.has(row),
    toggle: (row) =>
      setSel((s) => {
        const n = new Set(s)
        if (!n.delete(row)) n.add(row)
        return n
      }),
    setMany: (rows, on) =>
      setSel((s) => {
        const n = new Set(s)
        for (const row of rows) {
          if (on) n.add(row)
          else n.delete(row)
        }
        return n
      }),
    clear: () => setSel(new Set()),
    list: [...sel],
  }
}

function HeadBox({ sel, all }: { sel: Selection; all: number[] }) {
  const on = all.length > 0 && all.every((r) => sel.has(r))
  return (
    <input
      type="checkbox"
      aria-label="Select every unjudged duplicate on this page"
      disabled={all.length === 0}
      checked={on}
      onChange={(e) => sel.setMany(all, e.target.checked)}
    />
  )
}

function RowBox({ sel, row }: { sel: Selection; row: { duplicate?: boolean; row_index: number } }) {
  if (!row.duplicate) return null
  return (
    <input
      type="checkbox"
      aria-label="Select this duplicate"
      checked={sel.has(row.row_index)}
      onChange={() => sel.toggle(row.row_index)}
    />
  )
}

/** One verdict for exactly the rows ticked — no more. */
function SelectionBar({ sel, uploadId }: { sel: Selection; uploadId: string }) {
  const queryClient = useQueryClient()
  const resolve = useMutation({
    mutationFn: (verdict: 'genuine' | 'not_duplicate') =>
      postJson<{ rows: number[]; skipped: number[] }>(
        `/uploads/${uploadId}/duplicates/resolve`,
        { row_indexes: sel.list, verdict },
      ),
    onSuccess: (d, verdict) => {
      // the counts come from the RESPONSE, not from what was ticked
      const skipped = d.skipped?.length ?? 0
      showToast({
        color: 'green',
        title: `${d.rows?.length ?? 0} row(s) marked ${
          verdict === 'genuine' ? 'genuine duplicate' : 'not a duplicate'}`,
        message:
          (skipped ? `${skipped} already had that verdict. ` : '')
          + 'Nothing was deleted — the verdict is recorded and reversible.',
      })
      sel.clear()
      for (const k of ['upload-rows', 'upload-companies', 'raw-summary']) {
        void queryClient.invalidateQueries({ queryKey: [k] })
      }
    },
    onError: (e) =>
      showToast({
        color: 'red',
        title: 'Could not record that',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })
  if (!sel.list.length) return null
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md bg-blue-50 px-2 py-1.5">
      <span className="text-sm font-semibold">{sel.list.length} selected on this page</span>
      <Btn size="xs" kind="primary" loading={resolve.isPending}
        onClick={() => resolve.mutate('genuine')}>
        Genuine duplicate
      </Btn>
      <Btn size="xs" loading={resolve.isPending}
        onClick={() => resolve.mutate('not_duplicate')}>
        Not a duplicate
      </Btn>
      <Btn size="xs" kind="subtle" onClick={sel.clear}>Clear</Btn>
    </div>
  )
}

/** One expanded company's rows. Its own component so each open company pages
 *  independently — and so a collapsed company costs nothing at all. */
function CompanyRows({ entry, company, search, duplicates, dupMenu, onUndo, onEdit,
  pagerMiddle }: {
  entry: RawSummaryEntry
  company: string
  search: string
  duplicates: string
  dupMenu: DupMenu
  onUndo: (p: { row: number; field: string }) => void
  onEdit: (r: RawLedgerItem) => void
  pagerMiddle?: ReactNode
}) {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(PAGE_SIZE)
  const rowsQ = useUploadRows(entry.upload_id, { search, company, duplicates, page, size })
  const sel = useSelection([entry.upload_id, company, search, duplicates, page, size])
  const data = rowsQ.data
  // only a FLAGGED row can be judged, and the header box offers only the
  // UNJUDGED ones — select-all plus a verdict must not hand the server work
  // already done. Nothing is selectable on a side no verdict can be recorded
  // on: the checkboxes were a route to the same refused endpoint.
  const canReview = entry.dup_reviewable !== false
  const selectable = !canReview ? [] : ((data?.items ?? []) as {
    duplicate?: boolean
    row_index: number
    dup_verdict?: string | null
  }[])
    .filter((r) => r.duplicate && !r.dup_verdict)
    .map((r) => r.row_index)
  if (!data) return <p className="p-2 text-xs text-muted-foreground">Loading…</p>
  return (
    <div className="space-y-1.5 px-2 pb-2">
      {canReview && <SelectionBar sel={sel} uploadId={entry.upload_id} />}
      {(pagerMiddle || data.total > size) && (
        <TablePager total={data.total} page={page} size={size}
          onPage={setPage} onSize={setSize} middle={pagerMiddle} />
      )}
      {data.kind === '26as' && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr>
                <th className={`${th} w-8`}>{canReview && <HeadBox sel={sel} all={selectable} />}</th>
                <th className={th}>#</th>
                <th className={th}>Deductor</th>
                <th className={th}>TAN</th>
                <th className={th}>Section</th>
                <th className={th}>Txn date</th>
                <th className={th}>Status</th>
                <th className={`${th} text-right`}>Amount</th>
                <th className={`${th} text-right`}>TDS</th>
                <th className={th}>In file</th>
              </tr>
            </thead>
            <tbody>
              {/* no duplicate highlight on 26AS: the deductor's own filing is
                  not processed and not mentioned (user decision, 13-Aug) */}
              {(data.items as Raw26ASItem[]).map((r) => (
                <tr key={r.row_index} className="border-t border-border/60">
                  <td className={td}>{canReview && <RowBox sel={sel} row={r} />}</td>
                  <td className={`${td} text-muted-foreground`}>{r.row_index}</td>
                  <td className={td}>{r.deductor}</td>
                  <td className={`${td} font-mono text-xs`}>{r.tan}</td>
                  <td className={td}><Chip>{r.section}</Chip></td>
                  <td className={td}>{r.txn_date}</td>
                  <td className={td}>{r.status}</td>
                  <td className={`${td} text-right`}><Money v={r.amount} /></td>
                  <td className={`${td} text-right`}><Money v={r.tds} /></td>
                  <td className={`${td} whitespace-nowrap text-xs text-muted-foreground`}>
                    {r.src_ref ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.kind === 'ledger' && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr>
                <th className={`${th} w-8`}>{canReview && <HeadBox sel={sel} all={selectable} />}</th>
                <th className={th}>#</th>
                <th className={th}>Date</th>
                <th className={th}>Doc no</th>
                <th className={th}>Party</th>
                <th className={`${th} text-right`}>TDS (₹)</th>
                <th className={th}>Narration</th>
                <th className={th}>In file</th>
              </tr>
            </thead>
            <tbody>
              {(data.items as RawLedgerItem[]).map((r) => {
                const signed = Number(r.debit) - Number(r.credit)
                return (
                  <tr key={r.row_index}
                    className={`border-t border-border/60 ${
                      r.duplicate ? 'bg-amber-50' : ''}`}>
                    <td className={td}>{canReview && <RowBox sel={sel} row={r} />}</td>
                    <td className={`${td} text-muted-foreground`}>{r.row_index}</td>
                    <td className={td}>{r.date ?? ''}</td>
                    <td className={`${td} font-mono text-xs`}>{r.docno ?? ''}</td>
                    <td className={td}>
                      <span className="flex flex-wrap items-center gap-1">
                        {r.payor}
                        {r.edited && (
                          <Chip tone="blue" title={Object.entries(r.edits)
                            .map(([f, e]) => `${f}: ${e.old ?? '—'} → ${e.new ?? '—'}`)
                            .join('  •  ')}>
                            edited
                          </Chip>
                        )}
                        {/* the one action that changes SOURCE DATA must be
                            undoable; a typo in a correction was permanent
                            short of re-uploading the file */}
                        {r.edited &&
                          Object.keys(r.edits).map((f) => (
                            <button key={f}
                              className="rounded px-1 text-xs text-muted-foreground hover:bg-muted"
                              title={`Put ${f} back to what the file said`}
                              onClick={() => onUndo({ row: r.row_index, field: f })}>
                              undo {f}
                            </button>
                          ))}
                        {r.duplicate && dupMenu(r.row_index, r.dup_verdict)}
                        <button
                          className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                          title="Correct this books row"
                          onClick={() => onEdit(r)}>
                          <Pencil size={13} />
                        </button>
                      </span>
                    </td>
                    <td className={`${td} text-right`}>
                      <Money v={signed.toFixed(2)} colorize={signed < 0} />
                    </td>
                    <td className={`${td} max-w-72`}>
                      <span className="block truncate text-xs text-muted-foreground">
                        {r.narration ?? ''}
                      </span>
                    </td>
                    <td className={`${td} whitespace-nowrap text-xs text-muted-foreground`}>
                      {r.src_ref ?? '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function CompanyCard({ entry, company, search, duplicates, onBulk, open, onToggle,
  dupMenu, onUndo, onEdit }: {
  entry: RawSummaryEntry
  company: RawCompany
  search: string
  duplicates: string
  onBulk: (company: string, verdict: 'genuine' | 'not_duplicate') => void
  open: boolean
  onToggle: () => void
  dupMenu: DupMenu
  onUndo: (p: { row: number; field: string }) => void
  onEdit: (r: RawLedgerItem) => void
}) {
  return (
    <div className="rounded-md border border-border">
      <div
        className="flex cursor-pointer items-center justify-between gap-2 px-2.5 py-1.5"
        onClick={onToggle}
      >
        <span className="flex min-w-0 items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="truncate text-sm font-semibold">{company.name}</span>
          <Chip>{fmt.format(company.rows)}</Chip>
          {company.names.length > 1 && (
            <Chip tone="violet" title={company.names.join('  •  ')}>
              {company.names.length} names in the file
            </Chip>
          )}
        </span>
        <span className="flex items-center gap-1.5">
          {duplicates === 'unreviewed' && entry.dup_reviewable !== false && (
            /* duplicates cluster in a payor — one company's double-posted
               vouchers are one story, so the company is the unit that can
               honestly be judged at once. Books only. */
            <>
              <Btn size="xs"
                onClick={() => onBulk(company.name, 'genuine')}
                className="!text-emerald-700">
                All {company.rows} genuine
              </Btn>
              <Btn size="xs" kind="subtle"
                onClick={() => onBulk(company.name, 'not_duplicate')}>
                None are
              </Btn>
            </>
          )}
          <Money v={company.tds ?? company.debit ?? '0'} bold />
        </span>
      </div>
      {open && (
        <CompanyRows entry={entry} company={company.name} search={search}
          duplicates={duplicates} dupMenu={dupMenu} onUndo={onUndo} onEdit={onEdit} />
      )}
    </div>
  )
}

function DupMenuButton({ verdict, onPick }: {
  verdict?: 'genuine' | 'not_duplicate' | null
  onPick: (v: 'genuine' | 'not_duplicate') => void
}) {
  const [open, setOpen] = useState(false)
  const tone: Tone = verdict === 'genuine' ? 'green' : verdict === 'not_duplicate' ? 'gray' : 'yellow'
  return (
    <span className="relative">
      <button
        className={`rounded px-1.5 py-0.5 text-xs font-medium ${
          tone === 'green' ? 'bg-emerald-100 text-emerald-800'
            : tone === 'gray' ? 'bg-muted text-muted-foreground'
              : 'bg-amber-100 text-amber-800'}`}
        onClick={() => setOpen((s) => !s)}
      >
        {verdict === 'genuine' ? 'genuine dup' : verdict === 'not_duplicate' ? 'not a dup' : 'dup?'}
      </button>
      {open && (
        <span className="absolute left-0 top-full z-20 mt-1 flex w-56 flex-col rounded-md border border-border bg-background py-1 text-xs shadow-lg">
          <button className="px-2 py-1 text-left hover:bg-muted"
            onClick={() => { setOpen(false); onPick('genuine') }}>
            Genuine duplicate (fix in books)
          </button>
          <button className="px-2 py-1 text-left hover:bg-muted"
            onClick={() => { setOpen(false); onPick('not_duplicate') }}>
            Not a duplicate
          </button>
        </span>
      )}
    </span>
  )
}

function RowsTab({ entry }: { entry: RawSummaryEntry }) {
  const [search, setSearch] = useState('')
  const debounced = useDebounced(search, 300)
  // Flat by default: Raw Data means the file as it arrived. Grouping is how
  // the work is done, not how the file IS, so it is one click away.
  const [view, setView] = useState<'flat' | 'grouped'>('flat')
  const [dupFilter, setDupFilter] = useState('')
  // A–Z, because Raw Data is asked "where is this company" with a name in hand.
  const [sort, setSort] = useState<'name' | 'amount'>('name')
  const [companyPage, setCompanyPage] = useState(1)
  const [companySize, setCompanySize] = useState(100)
  const companiesQ = useUploadCompanies(entry.upload_id, debounced, dupFilter, sort,
    companyPage, companySize)
  const companies = companiesQ.data
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<RawLedgerItem | null>(null)
  const [opened, setOpened] = useState<Set<string>>(new Set())

  // a search is a question about ROWS, so its hits open themselves — capped at
  // the first 5 (every auto-opened company mounts its own rows query)
  const autoOpen = useMemo(
    () => new Set(debounced
      ? (companies?.items ?? []).slice(0, AUTO_OPEN_LIMIT).map((c) => c.name)
      : []),
    [debounced, companies],
  )
  const isOpen = (name: string) => opened.has(name) !== autoOpen.has(name)
  const toggle = (name: string) =>
    setOpened((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })

  const undo = useMutation({
    mutationFn: (p: { row: number; field: string }) =>
      api(`/uploads/${entry.upload_id}/ledger-rows/${p.row}?field=${p.field}`,
        { method: 'DELETE' }),
    onSuccess: () => {
      showToast({
        color: 'blue',
        title: 'Correction undone',
        message: 'The row is back to what the file said, and anything matched on it '
          + 'moved back with it.',
      })
      for (const k of ['upload-rows', 'upload-companies', 'raw-summary',
        'difference', 'reconciliation', 'cleared']) {
        void queryClient.invalidateQueries({ queryKey: [k] })
      }
    },
    onError: (e) =>
      showToast({
        color: 'red',
        title: 'Could not undo the correction',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })

  const dupReview = useMutation({
    mutationFn: (p: { row: number; verdict: 'genuine' | 'not_duplicate' }) =>
      postJson(`/uploads/${entry.upload_id}/duplicates/resolve`, {
        row_indexes: [p.row],
        verdict: p.verdict,
        comment: null,
      }),
    onSuccess: () => {
      showToast({ color: 'green', title: 'Duplicate review recorded' })
      for (const k of ['upload-rows', 'upload-companies', 'raw-summary']) {
        void queryClient.invalidateQueries({ queryKey: [k] })
      }
    },
    onError: (e) => showToast({ color: 'red', title: String(e) }),
  })

  const bulk = useMutation({
    mutationFn: (p: { company: string; verdict: 'genuine' | 'not_duplicate' }) =>
      postJson<{ reviewed: number; skipped: number[] }>(
        `/uploads/${entry.upload_id}/duplicates/resolve-company`, p),
    onSuccess: (r) => {
      showToast({
        color: 'green',
        title: `${r.reviewed} duplicate(s) reviewed`,
        message:
          ((r.skipped?.length ?? 0) ? `${r.skipped.length} already had that verdict. ` : '')
          + 'Nothing was deleted — the verdict is recorded and reversible.',
      })
      for (const k of ['upload-rows', 'upload-companies', 'raw-summary']) {
        void queryClient.invalidateQueries({ queryKey: [k] })
      }
    },
    onError: (e) => showToast({
      color: 'red', title: 'Could not record that',
      message: e instanceof ApiError ? e.message : String(e),
    }),
  })

  const counts = companies?.duplicate_counts
  // one answer per file, from the server, read by every control on this tab
  const canReview = counts ? counts.reviewable !== false : entry.dup_reviewable !== false

  // Books only: on the 26AS side there is no verdict to offer — the server
  // refuses one — and a menu that 409s reads as work somebody still owes.
  const dupMenu: DupMenu = (row, verdict) =>
    canReview ? (
      <DupMenuButton verdict={verdict}
        onPick={(v) => dupReview.mutate({ row, verdict: v })} />
    ) : null

  const chip = (key: string, label: string, tone: Tone, n: number) =>
    n > 0 && (
      <button key={key}
        className="cursor-pointer"
        onClick={() => { setDupFilter(dupFilter === key ? '' : key); setCompanyPage(1) }}>
        <Chip tone={tone} solid={dupFilter === key}>{fmt.format(n)} {label}</Chip>
      </button>
    )

  const corr = companies?.corrections
  const seg = (on: boolean) =>
    `rounded px-2 py-0.5 text-xs font-medium ${on ? 'bg-background shadow' : 'text-muted-foreground'}`
  // the controls share the PAGER's row (one band): view toggle, grouped sort,
  // the books dup chips, search
  const controls = (
    <>
      <span className="flex rounded-md bg-muted p-0.5">
        <button className={seg(view === 'flat')} onClick={() => setView('flat')}>Rows</button>
        <button className={seg(view === 'grouped')} onClick={() => setView('grouped')}>
          By company
        </button>
      </span>
      {view === 'grouped' && (
        <span className="flex rounded-md bg-muted p-0.5">
          <button className={seg(sort === 'name')}
            onClick={() => { setSort('name'); setCompanyPage(1) }}>A–Z</button>
          <button className={seg(sort === 'amount')}
            onClick={() => { setSort('amount'); setCompanyPage(1) }}>Amount</button>
        </span>
      )}
      {/* books only: 26AS repeats are not processed and not mentioned */}
      {canReview && counts && counts.any > 0 && (
        <span className="flex items-center gap-1">
          {chip('unreviewed', 'unreviewed', 'yellow', counts.unreviewed)}
          {chip('genuine', 'genuine', 'green', counts.genuine)}
          {chip('not_duplicate', 'not a duplicate', 'gray', counts.not_duplicate)}
          {dupFilter && (
            <button className="text-xs text-muted-foreground hover:underline"
              onClick={() => { setDupFilter(''); setCompanyPage(1) }}>
              clear
            </button>
          )}
        </span>
      )}
      <span className="relative">
        <Search size={13} className="absolute left-1.5 top-1.5 text-muted-foreground" />
        <input className={`${input} w-48 pl-6`} placeholder="Search anything…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCompanyPage(1) }} />
      </span>
      <span className="max-w-36 truncate text-xs text-muted-foreground"
        title={`${entry.filename} — single source of truth for this file`}>
        {entry.filename}
      </span>
    </>
  )

  const totals = entry.totals ?? {}
  return (
    <div className="mt-3 space-y-3">
      <ChipStrip>
        {entry.side === '26as' ? (
          <>
            <StatChip label="Amount total" value={totals.amount ?? '0'} />
            <StatChip label="TDS total" value={totals.tds ?? '0'} />
          </>
        ) : (
          <>
            <StatChip label="TDS booked (Dr)" value={totals.debit ?? '0'} />
            <StatChip label="Credits (Cr)" value={totals.credit ?? '0'} />
          </>
        )}
        <StatChip label="Rows" value={entry.rows} />
        <StatChip label="Companies" value={companies?.total ?? 0} />
        {corr && corr.edited_rows > 0 && (
          <span className="flex items-center gap-0.5">
            <span className="whitespace-nowrap text-xs font-semibold text-orange-700">
              {correctionChipLabel(corr.edited_rows, corr.debit_delta)}
            </span>
            <InfoTip w={340}>
              {corr.edited_rows} books row(s) corrected — TDS booked shown{' '}
              {Number(corr.debit_delta) >= 0 ? 'up' : 'down'} by ₹
              {Math.abs(Number(corr.debit_delta)).toLocaleString('en-IN')} from the parsed
              total. Originals kept; the next run re-validates the closing balance.
            </InfoTip>
          </span>
        )}
      </ChipStrip>
      {/* books only, and the panel decides for itself whether it has anything
          to say: a file with no repeated vouchers renders nothing */}
      {canReview && <KeepOnePanel uploadId={entry.upload_id} />}
      {debounced && companies && (
        <p className="text-xs text-muted-foreground">
          {companies.total === 0
            ? 'No company has a row matching that.'
            : `${fmt.format(companies.total)} compan${companies.total === 1 ? 'y' : 'ies'} match — ${
                companies.total > AUTO_OPEN_LIMIT
                  ? `first ${AUTO_OPEN_LIMIT} opened below (open the rest as needed).`
                  : 'opened below.'
              }`}
        </p>
      )}
      {view === 'flat' ? (
        <CompanyRows entry={entry} company="" search={debounced} duplicates={dupFilter}
          dupMenu={dupMenu} onUndo={undo.mutate} onEdit={setEditing}
          pagerMiddle={controls} />
      ) : (
        <div className="space-y-1">
          {companies && (
            <TablePager total={companies.total} page={companyPage} size={companySize}
              onPage={setCompanyPage} onSize={setCompanySize} middle={controls} />
          )}
          {(companies?.items ?? []).map((c) => (
            <CompanyCard key={c.name} entry={entry} company={c} search={debounced}
              duplicates={dupFilter}
              onBulk={(name, verdict) => bulk.mutate({ company: name, verdict })}
              open={isOpen(c.name)} onToggle={() => toggle(c.name)}
              dupMenu={dupMenu} onUndo={undo.mutate} onEdit={setEditing} />
          ))}
          {companies && companies.total > companySize && (
            <TablePager total={companies.total} page={companyPage} size={companySize}
              onPage={setCompanyPage} onSize={setCompanySize} compact />
          )}
        </div>
      )}
      {editing && (
        <LedgerEditModal uploadId={entry.upload_id} row={editing}
          onClose={() => setEditing(null)} />
      )}
    </div>
  )
}

function OpenChooser({ periodId }: { periodId: string }) {
  const { open } = useWorkspaceSession()
  const navigate = useNavigate()
  const runsQ = useRuns(periodId)
  const complete = (runsQ.data ?? []).filter((r) => r.status === 'complete')
  const params = useParams()
  const base = `/w/${params.clientId}/${periodId}`
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        Nothing is loaded by default. Choose to <b>upload fresh data</b> or <b>open a
        previous run</b> — this keeps every session starting from a clean slate.
      </div>
      <div className="flex flex-wrap gap-2">
        <Link to={`${base}/upload`}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90">
          <Database size={16} /> Upload data
        </Link>
        {complete.length > 0 && (
          <Btn onClick={() => {
            open(periodId)
            void navigate(`${base}/reconciliation?tab=sheet&run=${complete[0].id}`)
          }}>
            Open latest run ({runTag(complete[0].id)})
          </Btn>
        )}
      </div>
      {complete.length > 1 && (
        <p className="text-sm text-muted-foreground">
          Earlier runs:{' '}
          {complete.slice(1).map((r) => (
            <button key={r.id}
              className="mr-1 rounded px-1.5 py-0.5 text-xs text-primary hover:bg-muted"
              onClick={() => {
                open(periodId)
                void navigate(`${base}/reconciliation?tab=sheet&run=${r.id}`)
              }}>
              {runTag(r.id)}
            </button>
          ))}
        </p>
      )}
    </div>
  )
}

/** Verdicts recorded on a file BEFORE its side stopped being reviewable.
 *  Still in force, still disposing of entries through a review nobody can now
 *  perform or revisit — rendered for as long as one verdict is left standing.
 *  Kept, not deleted: who decided what stays; the withdrawal carries the name
 *  of whoever presses this. */
function RetireDupReview({ entry }: { entry: RawSummaryEntry }) {
  const qc = useQueryClient()
  const n = entry.dup_verdicts_in_force ?? 0
  const retire = useMutation({
    mutationFn: () =>
      postJson<{ retired: number; entries_released: number }>(
        `/uploads/${entry.upload_id}/duplicates/retire`, {}),
    onSuccess: (r) => {
      showToast({
        color: 'green',
        title: `${r.retired} verdict(s) withdrawn`,
        message: `${r.entries_released} entr${r.entries_released === 1 ? 'y' : 'ies'} no `
          + 'longer disposed of by them. Nothing was deleted — who decided what is kept.',
      })
      for (const k of ['upload-rows', 'upload-companies', 'raw-summary']) {
        void qc.invalidateQueries({ queryKey: [k] })
      }
    },
    onError: (e) => showToast({
      color: 'red', title: 'Could not withdraw them',
      message: e instanceof ApiError ? e.message : String(e),
    }),
  })
  return (
    <div className="flex items-center gap-2 rounded-md border border-orange-300 px-2 py-1.5">
      <AlertTriangle size={15} className="shrink-0 text-orange-600" />
      <p className="flex-1 text-xs">
        <b>{fmt.format(n)}</b> verdict{n === 1 ? '' : 's'} on {entry.filename}{' '}
        {n === 1 ? 'is' : 'are'} still disposing of entries through a 26AS review nobody
        can now perform or revisit. Who decided what is kept either way.
      </p>
      <Btn size="xs" kind="warning" loading={retire.isPending} onClick={() => retire.mutate()}>
        Withdraw {n} verdict{n === 1 ? '' : 's'}
      </Btn>
    </div>
  )
}

export function RawDataPage() {
  const { periodId } = useParams()
  const { sides } = useWorkspaceSession()
  // either side is enough to show the viewer — and ONLY that side, which is
  // why `loaded` goes on down rather than stopping here as a gate
  const loaded = sides(periodId ?? '')
  if (!loaded.source && !loaded.book) return <OpenChooser periodId={periodId ?? ''} />
  return <RawDataInner loaded={loaded} />
}

function RawDataInner({ loaded }: { loaded: Record<Side, boolean> }) {
  const { periodId } = useParams()
  const summaryQ = useRawSummary(periodId ?? '')
  const periodSummaryQ = usePeriodSummary(periodId ?? '')
  const dupPolicy = periodSummaryQ.data?.org_policy.duplicate_policy ?? 'warn'
  // every total below foots to THIS session's files. Passing the gate is not
  // permission to show the other side's ledger.
  const entries = visibleRawEntries(summaryQ.data ?? [], loaded)
  const source = entries.find((e) => e.side === '26as')
  const book = entries.find((e) => e.side === 'book')
  const uploadsQ = useUploads(periodId ?? '')
  const bookUpload = (uploadsQ.data ?? []).find((u) => u.id === book?.upload_id)
  const carried = bookUpload?.parse_stats?.carried_verdicts
  const notices = rawNotices(entries, dupPolicy, carried)
  const chip = noticeChip(entries, carried)
  const inForce = entries.filter((e) => e.dup_reviewable === false
    && (e.dup_verdicts_in_force ?? 0) > 0)
  const [tab, setTab] = useState<string | null>(null)
  const [noticesOpen, setNoticesOpen] = useState(false)
  const active = tab ?? (source ? '26as' : 'book')

  const tabBtn = (value: string, label: ReactNode, disabled = false) => (
    <button
      key={value}
      disabled={disabled}
      onClick={() => setTab(value)}
      className={`flex-1 border-b-2 px-3 py-2 text-sm ${
        active === value && !disabled
          ? 'border-primary font-semibold'
          : 'border-transparent text-muted-foreground'
      } ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:text-foreground'}`}
    >
      {label}
    </button>
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Database size={18} />
        <h2 className="text-base font-bold">Raw Data</h2>
        {/* the notice chip: its colour is the workflow signal (orange while
            any group is unreviewed — the Block gate's own condition); the
            full sentences live in the click popover */}
        {chip && (
          <span className="relative">
            <button onClick={() => setNoticesOpen((s) => !s)}>
              <Chip tone={chip.color as Tone} solid={chip.color === 'yellow'}
                className="cursor-pointer">
                <AlertTriangle size={12} className="mr-1" /> {chip.label}
              </Chip>
            </button>
            {noticesOpen && (
              <span className="absolute left-0 top-full z-30 mt-1 flex w-[560px] max-w-[90vw] flex-col gap-1 rounded-md border border-border bg-background p-2 shadow-lg">
                {notices.map((nt) => (
                  <span key={nt.key}
                    className="flex items-center gap-2 rounded border border-border px-2 py-1">
                    <AlertTriangle size={14} className="shrink-0 text-orange-600" />
                    <span className="flex-1 text-xs">{nt.text}</span>
                    {nt.badges.map((b) => (
                      <Chip key={b.label} tone={b.color as Tone} solid={b.filled}>
                        {b.label}
                      </Chip>
                    ))}
                  </span>
                ))}
              </span>
            )}
          </span>
        )}
      </div>

      {/* the withdraw prompt keeps its own row: it carries a named ACTION,
          not information, and an action does not belong behind a click */}
      {inForce.length > 0 && (
        <div className="space-y-1">
          {inForce.map((e) => <RetireDupReview key={e.upload_id} entry={e} />)}
        </div>
      )}

      {summaryQ.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!summaryQ.isLoading && entries.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No parsed uploads in this period yet — upload files first.
        </p>
      )}

      {entries.length > 0 && (
        <div>
          <div className="flex border-b border-border">
            {tabBtn('26as', <>26AS {source && <Chip className="ml-1">{fmt.format(source.rows)}</Chip>}</>, !source)}
            {/* AIS/TIS: spec §13-2 is an open owner decision — placeholders,
                honestly disabled, never dead links */}
            {tabBtn('ais', 'AIS', true)}
            {tabBtn('tis', 'TIS', true)}
            {tabBtn('book', <>Book Data {book && <Chip className="ml-1">{fmt.format(book.rows)}</Chip>}</>, !book)}
          </div>
          {active === '26as' && source && <RowsTab entry={source} />}
          {active === 'book' && book && <RowsTab entry={book} />}
        </div>
      )}
    </div>
  )
}
